import React, { createContext, useContext, useEffect, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'
import { LoadingSpinner, TermsAndConditionsModal } from '../../components'
import { LoadingSpinnerMeta, TermsAndConditionsMeta } from '../../data'
import { Training, User } from '../../data/types'
import { isSubscribedToPushNotifications, subscribeToPushNotifications } from '../../utils/pushNotifications'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'
const IIUK_BASE_URL = process.env.REACT_APP_IIUK_BASE_URL || 'https://iiuk.org'
const IIUK_SSO_REDIRECT_URL = `${IIUK_BASE_URL}/Pages/Festivals/ivc_redirect.php?auto=1`
const IIUK_SSO_SIGN_IN_URL = `${IIUK_BASE_URL}/?fn=${btoa(IIUK_SSO_REDIRECT_URL)}`

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    loggingOut: boolean;
    incompletePoliciesCount: number;
    incompletePolicies: Training[];
    authError: string | null;
    login: (navigate: NavigateFunction) => void;
    logout: (navigate: NavigateFunction) => void;
    checkAuth: () => Promise<void>;
    acceptTerms: () => Promise<void>;
    refreshIncompletePolicies: () => Promise<void>;
    clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [user, setUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const [loggingOut, setLoggingOut] = useState(false)
    const [showTermsModal, setShowTermsModal] = useState(false)
    const [acceptingTerms, setAcceptingTerms] = useState(false)
    const [incompletePoliciesCount, setIncompletePoliciesCount] = useState(0)
    const [incompletePolicies, setIncompletePolicies] = useState<Training[]>([])
    const [authError, setAuthError] = useState<string | null>(null)

    const clearAuthError = () => setAuthError(null)

    const checkAuth = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/user`, {
                credentials: 'include', // Important for session cookies
                headers: {
                    "Accept": "application/json"
                }
            })

            if (response.ok) {
                const data = await response.json()
                if (data.user) {
                    setUser(data.user)
                    setIsAuthenticated(true)

                    // Check if user needs to accept terms
                    if (!data.user.terms_accepted_at) {
                        setShowTermsModal(true)
                    }
                } else {
                    setUser(null)
                    setIsAuthenticated(false)
                }
            } else {
                setUser(null)
                setIsAuthenticated(false)
            }
        } catch (error) {
            console.error('Auth check failed:', error)
            setUser(null)
            setIsAuthenticated(false)
        } finally {
            setLoading(false)
        }
    };

    const login = async (navigate: NavigateFunction) => {
        // // Redirect to OAuth flow
        // window.location.href = `${API_BASE_URL}/auth/oauth/redirect`

        // Use SSO flow instead since OAuth doesn't create IIUK session, and we need that when redirecting back to IIUK for user to select different household member
        window.location.href = IIUK_SSO_SIGN_IN_URL
    }

    const acceptTerms = async () => {
        setAcceptingTerms(true)
        try {
            const response = await fetch(`${API_BASE_URL}/auth/accept-terms`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            })

            if (response.ok) {
                const data = await response.json()
                if (data.user) {
                    setUser(data.user)
                    setShowTermsModal(false)
                }
            } else {
                console.error('Failed to accept terms')
            }
        } catch (error) {
            console.error('Error accepting terms:', error)
        } finally {
            setAcceptingTerms(false)
        }
    }

    const handleDisagreeTerms = () => {
        // Redirect to external form
        window.location.href = 'https://iv.iiuk.org/vms-form'
    }

    const refreshIncompletePolicies = async () => {
        if (!user?.volunteer_csid) {
            setIncompletePoliciesCount(0)
            setIncompletePolicies([])
            return
        }

        try {
            const response = await fetch(`${API_BASE_URL}/volunteer/${user.volunteer_csid}/policy/incomplete`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`Error: ${response.status}`)
            }
            const result = await response.json()
            setIncompletePoliciesCount(result.length)
            setIncompletePolicies(result)
        }
        catch (err) {
            console.error('Error fetching incomplete policies:', err)
            setIncompletePoliciesCount(0)
            setIncompletePolicies([])
        }
    }

    const logout = async (navigate: NavigateFunction) => {
        setLoggingOut(true)
        try {
            await fetch(`${API_BASE_URL}/auth/oauth/logout`, {
                method: 'POST',
                credentials: 'include',
            })
        } catch (error) {
            console.error('Logout failed:', error)
        } finally {
            setLoggingOut(false)
        }

        // Clear auth state
        setIsAuthenticated(false)
        setUser(null)

        // Navigate to login
        navigate('/login', { replace: true })
    }

    // Apply theme on mount (before loading screen)
    useEffect(() => {
        const storedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null
        const mode = storedTheme || 'system'

        let shouldBeDark = false
        if (mode === 'system') {
            shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        } else {
            shouldBeDark = mode === 'dark'
        }

        if (shouldBeDark) {
            document.documentElement.classList.add('dark')
        } else {
            document.documentElement.classList.remove('dark')
        }
    }, [])

    // Check auth on mount
    useEffect(() => {
        checkAuth()
    }, [])

    // Fetch incomplete policies when user is authenticated
    useEffect(() => {
        if (isAuthenticated && user?.volunteer_csid) {
            refreshIncompletePolicies()
        }
    }, [isAuthenticated, user?.volunteer_csid])

    // Automatically enable push notifications when user signs in
    useEffect(() => {
        const enableNotificationsOnLogin = async () => {
            if (isAuthenticated && user) {
                try {
                    // Only auto-enable if user hasn't been asked yet (permission is "default")
                    if ('Notification' in window && Notification.permission === 'default') {
                        // Check if already subscribed
                        const alreadySubscribed = await isSubscribedToPushNotifications()

                        if (!alreadySubscribed) {
                            // Attempt to automatically subscribe
                            console.log('Attempting to automatically enable push notifications...')
                            await subscribeToPushNotifications()
                            console.log('Push notifications enabled automatically')
                        }
                    } else {
                        console.log('Skipping auto-enable: permission already', Notification.permission)
                    }
                } catch (error) {
                    // Silently fail - don't interrupt user experience
                    // User can manually enable notifications later if needed
                    console.log('Could not automatically enable push notifications:', error)
                }
            }
        }

        enableNotificationsOnLogin()
    }, [isAuthenticated, user])

    // Handle OAuth callback
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (params.get('auth') === 'success') {
            // Authentication successful, check user
            checkAuth()
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname)
        } else if (params.get('auth') === 'error') {
            const message = params.get('message')
            console.error('OAuth error:', message)
            setAuthError(message || 'An authentication error occurred.')
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname)
        }

    }, [])

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
                <LoadingSpinner meta={LoadingSpinnerMeta} />
            </div>
        )
    }

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, loggingOut, incompletePoliciesCount, incompletePolicies, authError, login, logout, checkAuth, acceptTerms, refreshIncompletePolicies, clearAuthError }}>
            {!showTermsModal && children}
            <TermsAndConditionsModal
                isOpen={showTermsModal}
                onAgree={acceptTerms}
                onDisagree={handleDisagreeTerms}
                meta={TermsAndConditionsMeta}
                isLoading={acceptingTerms}
            />
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used within AuthProvider')
    return ctx
}
